'use client'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, Link, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'

import { SearchForProducts } from 'components/form/SearchForProducts'
import { CartPreview } from 'components/nav/CartPreview'
import { Logo } from 'components/nav/Logo'
import { Menu } from 'components/nav/Menu'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { AppDispatch } from 'store'
import { selectOrderState } from 'store/partials/cart/cartSelectors'
import { getCart, resetCart } from 'store/partials/cart/cartSlice'
import { getLocations } from 'store/partials/shop/shopSlice'
import { collectCustomerDataWithSegment } from 'utils/collectUserDataWithSegment'
import { STORAGE_KEYS } from 'utils/constants'
import { parseRouterQuery } from 'utils/parseRouterQuery'
import { getFromStorage } from 'utils/storage'

import CategoriesMenu from '../CategoriesMenu/FullMenu'
import MobileCategoriesMenu from '../CategoriesMenu/MobileMenu'
import { StyledMenuButton } from '../MenuButton/styles'
import { NotLoggedInAvatar } from '../NavigationAvatars'
import LoggedInAvatar from '../NavigationAvatars/LoggedInAvatar'

import { StyledNav } from './styles'

export const NavigationClient = (): JSX.Element => {
  const router = useRouter()
  const { customerInfo } = useCustomerInfo()
  const dispatch = useDispatch<AppDispatch>()
  const { cart } = useSelector(selectOrderState)

  const isLoggedIn = customerInfo.customer !== undefined

  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false)

  useEffect(() => {
    dispatch(getLocations())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      collectCustomerDataWithSegment(customerInfo.customer)
    }
  }, [customerInfo.customer])

  useEffect(() => {
    const orderId = getFromStorage(STORAGE_KEYS.orderId)
    const isProlongation = parseRouterQuery(router.query.prolongation_id)
    const pattern = new RegExp(`(\/payments\/*.*\/success)`)
    const isOnPaymentPage = pattern.test(router.pathname)
    const shouldResetCart = isOnPaymentPage && orderId && !isProlongation

    if (shouldResetCart) {
      dispatch(resetCart())
    } else if (orderId && !cart) {
      dispatch(getCart(Number(orderId)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, router.query, router.pathname])

  return (
    <StyledNav>
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <MobileCategoriesMenu />
        <Logo />
        <CategoriesMenu />

        <Flex width="100%" justifyContent="flex-end">
          {router.route !== '/' && (
            <Box width="70%" marginLeft="auto" marginRight="auto" display={{ base: 'none', md: 'block' }}>
              <SearchForProducts redirectTo={router.pathname.includes('/bundles') ? 'bundles' : 'browse'} />
            </Box>
          )}

          <Flex alignItems="center" marginLeft="26px">
            {isLoggedIn ? <LoggedInAvatar /> : <NotLoggedInAvatar />}

            <Box position="relative" marginRight="10px" marginLeft="26px">
              {cart?.orderItems && cart?.orderItems.length >= 1 && (
                <Flex
                  width="18px"
                  height="18px"
                  backgroundColor="green.a200"
                  position="absolute"
                  zIndex={2}
                  right="0"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="100%"
                >
                  <Text lineHeight="1" fontSize="xs" color="white" margin="0" padding="0">
                    {cart?.orderItems.length}
                  </Text>
                </Flex>
              )}
              <Link href="/cart" _focus={{ boxShadow: 'none' }}>
                <StyledMenuButton
                  onMouseEnter={() => setIsCartPreviewOpen(true)}
                  onMouseLeave={() => setIsCartPreviewOpen(false)}
                  isOpen={isCartPreviewOpen}
                >
                  <img src="/icons/cart.svg" alt="" />
                </StyledMenuButton>
              </Link>
              {isCartPreviewOpen && <CartPreview />}
            </Box>

            <Menu isLoggedIn={isLoggedIn} isCartPreviewOpen={isCartPreviewOpen} />
          </Flex>
        </Flex>
      </Flex>
    </StyledNav>
  )
}
